/**
 * User-friendly fallback messages keyed by HTTP status code.
 *
 * When the server response doesn't carry a usable message (or the body isn't
 * JSON at all), we show these instead of a raw "HTTP 403".
 */
const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  401: 'Please log in to continue.',
  402: 'This feature requires an upgraded plan.',
  403: 'You are not authorized to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This conflicts with an existing resource. Please refresh and try again.',
  413: 'The upload is too large.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on our end. Please try again later.',
  502: 'The server is temporarily unreachable. Please try again shortly.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
}

/**
 * Structured API error thrown by the `api.*` helpers on non-2xx responses.
 *
 * Carries the HTTP status, the machine-readable `code` from the server's
 * `{ error: { code, message } }` envelope, and a human-readable `message`
 * (either the server's client-safe message or a friendly status fallback).
 */
export class ApiError extends Error {
  /** HTTP status code (e.g. 401, 404, 500). */
  statusCode: number
  /** Machine-readable error code from the server (e.g. "NOT_FOUND"). */
  code?: string

  constructor(message: string, statusCode: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.code = code
  }

  /**
   * Build an `ApiError` from a non-ok `Response`.
   *
   * Tries to parse the server's JSON error envelope (`{ error: { code,
   * message } }`). Falls back to a user-friendly message based on the status
   * code, then to the raw status text.
   */
  static async fromResponse(response: Response): Promise<ApiError> {
    let serverMessage: string | undefined
    let serverCode: string | undefined

    try {
      const body = await response.json()
      if (body?.error) {
        serverMessage = body.error.message
        serverCode = body.error.code
      } else if (typeof body?.message === 'string') {
        serverMessage = body.message
      }
    } catch {
      // Body wasn't JSON — fall through to status-based message.
    }

    const message =
      serverMessage ||
      STATUS_MESSAGES[response.status] ||
      response.statusText ||
      `Request failed (HTTP ${response.status})`

    return new ApiError(message, response.status, serverCode)
  }

  /** Return a user-friendly message for this error's status code. */
  get userMessage(): string {
    return STATUS_MESSAGES[this.statusCode] || this.message
  }
}

/**
 * Extract a display-ready error string from an unknown thrown value.
 *
 * For `ApiError` instances, returns the structured message (which is already
 * user-friendly). For everything else, falls back through the usual chain.
 */
export function normalizeError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  if (
    err !== null &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as Record<string, unknown>).message === 'string'
  ) {
    return (err as Record<string, unknown>).message as string
  }
  return 'An unexpected error occurred'
}
